import React from 'react';

import { ButtonVariant } from 'apps/auth/components/common/Button/Button';

import { CenterContainer, Container, HeaderText, MainText, RowButtonContainer, SecondarySmallText, SecondaryText } from './styles';
import { AnimatedIconFail } from '../../AnimatedIcon/AnimatedIconFail';
import { AnimatedIconSuccess } from '../../AnimatedIcon/AnimatedIconSuccess';
import { Button } from '../../Button/Button';
import type { ButtonProps } from '../../Button/Button';
import { DiskButton } from '../../DiskButton/DiskButton';
import { Icon, type IconColor } from '../../Icon/Icon';
import { FixedBottomPanel } from '../FixedBottomPanel/FixedBottomPanel';
import { NavBar } from '../NavBar/NavBar';

type NavBarAlign = 'left' | 'right';

type ButtonType = ButtonProps['type'];

interface InfoPageProps {
    dataTestId?: string;
    insideModalComponent?: boolean;
    backUrl?: string;
    isTagA?: boolean;
    leftCloseIcon?: boolean;
    diskButtonOnClick?: React.MouseEventHandler<HTMLButtonElement>;
    topButtonCaption?: string;
    topButtonType?: ButtonType;
    topButtonOnClick?: React.MouseEventHandler<HTMLButtonElement>;
    topButtonLink?: string;
    topButtonDataTestId?: string;
    iconName?: string;
    iconSrc?: string;
    iconColor?: IconColor;
    headerText?: React.ReactNode;
    secondaryText?: React.ReactNode;
    secondarySmallText?: React.ReactNode;
    mainText?: React.ReactNode;
    hasBorder?: boolean;
    breakWord?: boolean;
    buttonRowType?: ButtonType;
    buttonRowTarget?: string;
    buttonRowRel?: string;
    buttonRowCaption?: React.ReactNode;
    buttonRowOnClick?: React.MouseEventHandler<HTMLButtonElement>;
    buttonRowHref?: string;
    primaryButtonCaption?: React.ReactNode;
    primaryButtonType?: ButtonType;
    primaryButtonOnClick?: React.MouseEventHandler<HTMLButtonElement>;
    primaryButtonLink?: string;
    animatedIconSuccess?: boolean;
    animatedIconFail?: boolean;
    primaryButtonDataTestId?: string;
    primaryButtonVariant?: ButtonVariant;
    insideStories?: boolean;
}

export const InfoPage: React.FC<InfoPageProps> = (props) => {
    const setNavBarAlign = (): NavBarAlign => {
        if (props.backUrl) {
            return 'left';
        }
        return 'right';
    };

    return (
        <Container data-testid={props.dataTestId} insideModalComponent={props.insideModalComponent}>
            {(props.backUrl || props.topButtonCaption) && (
                <NavBar align={setNavBarAlign()} insideStories={props.insideStories}>
                    {props.backUrl && (
                        <DiskButton
                            type={props.isTagA ? 'tagA' : undefined}
                            to={props.backUrl}
                            href={props.backUrl}
                            iconName={props.leftCloseIcon ? 'cross' : 'arrow-back'}
                            onClick={props.diskButtonOnClick}
                        />
                    )}
                    {props.topButtonCaption && (
                        <Button
                            type={props.topButtonType}
                            variant="tertiary"
                            size="tight"
                            caption={props.topButtonCaption}
                            onClick={props.topButtonOnClick}
                            href={props.topButtonLink}
                            dataTestId={props.topButtonDataTestId}
                        />
                    )}
                </NavBar>
            )}
            <CenterContainer>
                <div>
                    {props.iconName && <Icon name={props.iconName} color={props.iconColor} size="80px" />}
                    {!props.iconName && props.iconSrc && <img src={props.iconSrc} alt="" />}
                    {!props.iconName && !props.iconSrc && props.animatedIconSuccess && <AnimatedIconSuccess />}
                    {!props.iconName && !props.iconSrc && props.animatedIconFail && <AnimatedIconFail />}
                </div>
                {props.headerText && <HeaderText>{props.headerText}</HeaderText>}
                {props.secondaryText && <SecondaryText>{props.secondaryText}</SecondaryText>}
                {props.secondarySmallText && <SecondarySmallText>{props.secondarySmallText}</SecondarySmallText>}
                {props.mainText && (
                    <MainText hasBorder={props.hasBorder} breakWord={props.breakWord}>
                        {props.mainText}
                    </MainText>
                )}
                {props.buttonRowCaption && (
                    <RowButtonContainer>
                        <Button
                            type={props.buttonRowType}
                            variant="row"
                            size="tight"
                            negativeMargin
                            caption={props.buttonRowCaption}
                            onClick={props.buttonRowOnClick}
                            target={props.buttonRowTarget}
                            href={props.buttonRowHref}
                            rel={props.buttonRowRel}
                        />
                    </RowButtonContainer>
                )}
            </CenterContainer>
            {props.primaryButtonCaption && (
                <FixedBottomPanel isRightAligned>
                    <Button
                        dataTestId={props.primaryButtonDataTestId}
                        type={props.primaryButtonType}
                        raised="true"
                        caption={props.primaryButtonCaption}
                        onClick={props.primaryButtonOnClick}
                        href={props.primaryButtonLink}
                        to={props.primaryButtonLink}
                        variant={props.primaryButtonVariant}
                    />
                </FixedBottomPanel>
            )}
        </Container>
    );
};
